import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from './client'

/**
 * Auth domain — register / login / logout / me.
 *
 * The session is a HttpOnly cookie set by the backend on /api/auth/login.
 * The frontend never holds a token. `meQuery` is the single source of
 * truth for "am I logged in"; everything else reads from it.
 */

export interface Me {
  id: string
  email: string
  name?: string
}

/** TanStack Query options factory for the current session. */
export const meQuery = () =>
  queryOptions({
    queryKey: ['me'] as const,
    queryFn: () => api.get<Me>('/me'),
    staleTime: 5 * 60 * 1000, // 5 min — session is stable across navigation
    retry: false, // 401 means logged out; don't retry
  })

export interface LoginInput {
  email: string
  password: string
}

export interface RegisterInput {
  email: string
  code: string
  password: string
  firstName: string
  lastName: string
  /** `City, Country`；城邦只填国家。后端只把国家名转给 LoopCV。 */
  location?: string
}

export interface RegisterCodeInput {
  email: string
}

export interface ResetPasswordInput {
  email: string
  code: string
  password: string
}

/** POST /api/auth/register/send-code → 204. */
export function useSendRegisterCodeMutation() {
  return useMutation({
    mutationFn: (input: RegisterCodeInput) => api.post<void>('/auth/register/send-code', input),
  })
}

/** POST /api/auth/register → 204 + Set-Cookie. */
export function useRegisterMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: RegisterInput) => api.post<void>('/auth/register', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me'] })
    },
  })
}

/** POST /api/auth/login → 204 + Set-Cookie. */
export function useLoginMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: LoginInput) => api.post<void>('/auth/login', input),
    onSuccess: () => {
      // Cookie is set; refresh `meQuery` so the router re-evaluates.
      void qc.invalidateQueries({ queryKey: ['me'] })
    },
  })
}

/** POST /api/auth/password/send-code → 204. Always 204, even for unknown emails. */
export function useSendPasswordResetCodeMutation() {
  return useMutation({
    mutationFn: (input: { email: string }) => api.post<void>('/auth/password/send-code', input),
  })
}

/** POST /api/auth/password/reset → 204; every existing session is dropped. */
export function useResetPasswordMutation() {
  return useMutation({
    mutationFn: (input: ResetPasswordInput) => api.post<void>('/auth/password/reset', input),
  })
}

/** POST /api/auth/logout → 204. */
export function useLogoutMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<void>('/auth/logout'),
    onSettled: () => {
      // Clear EVERYTHING: any cached data may have been user-specific.
      qc.clear()
    },
  })
}
